import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { InternalLinks } from "@/components/InternalLinks";
import { MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const BOOKING_URL = "/book";

const NearbyAreas = () => {
  const { ref, isVisible } = useScrollAnimation();
  const areas = [
    { zone: "Walnut Creek", cities: "Walnut Creek, Pleasant Hill, Lafayette, Orinda" },
    { zone: "Concord", cities: "Concord, Martinez, Clayton, Bay Point" },
    { zone: "San Ramon", cities: "San Ramon, Danville, Alamo, Blackhawk" },
    { zone: "Richmond", cities: "Richmond, Antioch, Brentwood, Pittsburg" },
  ];
  return (
    <section className="py-16 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-10 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Áreas que Servimos en el Condado de Contra Costa</h2>
          <p className="text-muted-foreground">Limpieza profesional a domicilio en todo el Condado de Contra Costa y sus alrededores.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {areas.map((area, i) => (
            <div
              key={i}
              className={cn("bg-background rounded-2xl p-6 border shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 text-center anim-fade-up", isVisible && "is-visible")}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <MapPin className="h-6 w-6 text-primary mx-auto mb-3" />
              <h3 className="font-bold text-primary mb-2">{area.zone}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{area.cities}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild size="lg" className="btn-pulse bg-primary text-primary-foreground hover:bg-primary-hover font-bold px-10 py-6 h-auto rounded-2xl shadow-lg">
            <a href={BOOKING_URL}>Reservar Ahora en Contra Costa</a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary/5 hover:text-primary font-bold px-10 py-6 h-auto rounded-2xl">
            <a href={BOOKING_URL}>Ver Mi Precio al Instante</a>
          </Button>
        </div>
      </div>
    </section>
  );
};

import { useEffect } from "react";

const ContraCostaSpanishPage = () => {
  useEffect(() => {
    localStorage.setItem("lastRegion", "contra-costa");
  }, []);

  return (
    <LocationContext.Provider value={{ slug: "contra-costa", name: "Contra Costa", type: "region", regionSlug: "contra-costa" }}>
      <SEO
        title="Limpieza del Hogar en Contra Costa, CA — Uplevel Cleaning"
        description="Servicio profesional de limpieza del hogar en Walnut Creek, Concord, San Ramon, Danville, Richmond y todo el Condado de Contra Costa. Precios planos, reserva en 60 segundos, garantía del 200%."
        keywords="limpieza del hogar contra costa, servicio de limpieza walnut creek, limpieza profunda concord ca, empleada doméstica san ramon, maid service contra costa español, limpieza danville orinda lafayette, servicio de limpieza condado contra costa"
        url="https://uplevelcleaning.com/es/contra-costa"
        locationName="Contra Costa"
        regionSlug="contra-costa"
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: "Contra Costa", url: "/es/contra-costa" }
        ]}
        reviews={[
          { author: "Adriana M.", rating: 5, reviewBody: "Vivo en Walnut Creek y buscaba un servicio de limpieza confiable. ¡Uplevel Cleaning llegó puntual y dejó mi casa impecable! Los recomiendo al 100%.", datePublished: "2026-03-20" },
          { author: "Francisco V.", rating: 5, reviewBody: "Contraté el servicio quincenal para nuestra casa en San Ramon. El equipo es muy profesional y respetuoso. Excelente relación calidad-precio.", datePublished: "2026-02-15" },
          { author: "Mónica L.", rating: 5, reviewBody: "La limpieza profunda en Concord fue perfecta. El equipo trató cada rincón con atención y el precio fue exactamente lo que se indicó en línea.", datePublished: "2026-01-28" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName="Contra Costa" regionSlug="contra-costa" />

          {/* Local trust bar */}
          <div className="bg-primary/5 border-b border-primary/10 py-4">
            <div className="container flex flex-wrap justify-center gap-6 text-sm font-medium text-foreground">
              {[
                "Servicio Local en Contra Costa",
                "Walnut Creek, Concord, San Ramon y Más",
                "Garantía del 200%",
                "Precios Transparentes",
                "Reserva en 60 Segundos",
              ].map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" /> {item}
                </span>
              ))}
            </div>
          </div>

          <SpanishWhyUs cityName="Contra Costa" />
          <NearbyAreas />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region="contra-costa" />
          <Guarantee />
          <SpanishFAQ cityName="Contra Costa" />
          <InternalLinks />
          <SpanishFinalCTA cityName="Contra Costa" />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default ContraCostaSpanishPage;
